/**
 * <bundle-stack>
 *
 * Visually stacks 2+ bundle cards into a layered pile (top card fully visible,
 * subsequent cards peeking out below). Tap anywhere on the stack except the
 * top "Bundle" CTA to expand. After expansion, collapses once the viewport has
 * scrolled at least `--bundle-stack-collapse-scroll-px` (default 200px) away
 * from the scroll position captured when the listener attached.
 *
 * For a single card, just renders inline with no interactive stack behavior.
 */
class BundleStack extends HTMLElement {
  constructor() {
    super();
    this.expanded = false;
    this._scrollAtExpand = 0;
    this._toggleButton = null;
    this._onClick = this._onClick.bind(this);
    this._onToggleButtonClick = this._onToggleButtonClick.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._measure = this._measure.bind(this);
  }

  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll(':scope > .bundle-card'));
    if (this.cards.length === 0) {
      queueMicrotask(() => {
        this.cards = Array.from(this.querySelectorAll(':scope > .bundle-card'));
        if (this.cards.length === 0) return;
        this._initCards();
      });
      return;
    }
    this._initCards();
  }

  _initCards() {
    this.cards.forEach((card, i) => {
      card.style.setProperty('--stack-index', i);
      card.setAttribute('data-stack-index', String(i));
    });

    this._measure();
    this.classList.add('is-ready');
    this._initToggleButton();

    if (this.cards.length < 2) {
      this.classList.add('bundle-stack--single');
      return;
    }

    this.classList.add('bundle-stack--multi');
    this.addEventListener('click', this._onClick);
    window.addEventListener('resize', this._onResize, { passive: true });

    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(this._measure);
      this.cards.forEach((c) => this._ro.observe(c));
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    if (this._toggleButton) {
      this._toggleButton.removeEventListener('click', this._onToggleButtonClick);
      this._toggleButton = null;
    }
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    if (this._ro) this._ro.disconnect();
    if (this._scrollListenTimer) clearTimeout(this._scrollListenTimer);
  }

  _initToggleButton() {
    const maybeButton = this.nextElementSibling;
    if (!maybeButton || !maybeButton.classList.contains('bundle-stack__view-all')) return;

    this._toggleButton = maybeButton;
    this._toggleButton.addEventListener('click', this._onToggleButtonClick);
  }

  _onToggleButtonClick(e) {
    e.preventDefault();
    if (!this.expanded) this._expand();
  }

  _measure() {
    if (!this.cards || this.cards.length === 0) return;

    // Temporarily neutralize stack positioning so we can measure natural heights.
    const wasExpanded = this.classList.contains('is-expanded');
    this.classList.add('is-measuring');

    const heights = this.cards.map((c) => c.offsetHeight);
    const styles = getComputedStyle(this);
    const peek = parseFloat(styles.getPropertyValue('--bundle-stack-peek')) || 10;
    const gap = parseFloat(styles.getPropertyValue('--bundle-stack-gap')) || 8;

    let collapsedHeight = heights[0];
    heights.forEach((h, i) => {
      if (i === 0) return;
      collapsedHeight = Math.max(collapsedHeight, i * peek + h);
    });

    let expandedHeight = 0;
    heights.forEach((h, i) => {
      expandedHeight += h;
      if (i < heights.length - 1) expandedHeight += gap;
    });

    this.cards.forEach((card, i) => {
      const collapsedTop = i * peek;
      let expandedTop = 0;
      for (let j = 0; j < i; j += 1) expandedTop += heights[j] + gap;
      card.style.setProperty('--collapsed-top', `${collapsedTop}px`);
      card.style.setProperty('--expanded-top', `${expandedTop}px`);
    });

    this.style.setProperty('--collapsed-height', `${collapsedHeight}px`);
    this.style.setProperty('--expanded-height', `${expandedHeight}px`);

    this.classList.remove('is-measuring');
    if (wasExpanded) this.classList.add('is-expanded');
  }

  _onResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(this._measure, 150);
  }

  _onClick(e) {
    const cta = e.target.closest('.bundle-card__cta');
    const topCard = this.cards[0];

    if (this.expanded) {
      // When expanded, only the CTA does anything (open drawer for that bundle).
      // Clicks on card body do nothing — let the user tap a CTA or scroll away.
      if (!cta) {
        e.preventDefault();
      }
      return;
    }

    // Collapsed: tap on top card's real link opens the drawer (let it through).
    if (cta && topCard.contains(cta)) {
      if (cta.matches('a.bundle-card__cta[href]')) return;
      e.preventDefault();
      return;
    }

    // Tap on a CTA that belongs to a hidden card behind the top — block it
    // and just expand the stack instead.
    if (cta) {
      e.preventDefault();
      e.stopPropagation();
    }

    this._expand();
  }

  _expand() {
    if (this.expanded) return;
    this.expanded = true;
    this.classList.add('is-expanded');

    // Wait until after the expand transition before listening for scroll —
    // avoids any layout-shift-induced scroll being interpreted as user intent.
    if (this._scrollListenTimer) clearTimeout(this._scrollListenTimer);
    this._scrollListenTimer = setTimeout(() => {
      if (!this.expanded) return;
      this._scrollAtExpand = window.scrollY;
      window.addEventListener('scroll', this._onScroll, { passive: true });
    }, 50);
  }

  _collapse() {
    if (!this.expanded) return;
    this.expanded = false;
    this.classList.remove('is-expanded');
    window.removeEventListener('scroll', this._onScroll);
  }

  _onScroll() {
    const raw = getComputedStyle(this).getPropertyValue('--bundle-stack-collapse-scroll-px').trim();
    const parsed = parseFloat(raw);
    const threshold = Number.isFinite(parsed) && parsed > 0 ? parsed : 200;
    if (Math.abs(window.scrollY - this._scrollAtExpand) >= threshold) {
      this._collapse();
    }
  }
}

if (!customElements.get('bundle-stack')) {
  customElements.define('bundle-stack', BundleStack);
}
